import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.scss';
import { useModalStore } from '../../store/ModalStore';

interface ModalProps {
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
  noCross?: boolean;
  disableClose?: boolean; // Новый пропс для блокировки закрытия
}

const Modal: React.FC<ModalProps> = ({
  children,
  open,
  onClose,
  noCross,
  disableClose,
}) => {
  // const open = useModalStore((state) => state.open);
  // const setOpen = useModalStore((state) => state.setOpen);
  // const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!open) return;

    const container = document.querySelector('.container') as HTMLElement;
    const body = document.body;
    const html = document.documentElement;
    
    // Сохраняем позиции скролла
    const scrollY = window.scrollY || window.pageYOffset || html.scrollTop;
    const containerScrollTop = container?.scrollTop || 0;
    
    // Блокируем все виды скролла
    const preventScroll = (e: TouchEvent | WheelEvent) => {
      const target = e.target as HTMLElement;
      
      // Разрешаем скролл ТОЛЬКО внутри .modalContent (контент модалки)
      // НЕ разрешаем на backdrop (modalWrapper) или других элементах
      const modalContent = target.closest('[class*="modalContent"]');
      if (modalContent) {
        // Разрешаем скролл внутри модального контента
        return;
      }
      
      // Блокируем все остальные события (включая свайпы по backdrop)
      e.preventDefault();
      e.stopPropagation();
    };
    
    // Применяем блокировку через CSS
    if (container) {
      container.style.overflow = 'hidden';
    }
    
    // Фиксируем body с сохранением позиции (для предотвращения прыжка)
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none'; // Блокируем touch жесты на iOS
    
    html.style.overflow = 'hidden';
    html.style.touchAction = 'none';
    
    // Блокируем события на window (более надежно, чем document)
    window.addEventListener('touchmove', preventScroll as EventListener, { passive: false, capture: true });
    window.addEventListener('wheel', preventScroll as EventListener, { passive: false, capture: true });
    window.addEventListener('touchstart', preventScroll as EventListener, { passive: false, capture: true });
    
    return () => {
      // Удаляем обработчики
      window.removeEventListener('touchmove', preventScroll as EventListener, { capture: true });
      window.removeEventListener('wheel', preventScroll as EventListener, { capture: true });
      window.removeEventListener('touchstart', preventScroll as EventListener, { capture: true });
      
      // Восстанавливаем стили
      if (container) {
        container.style.overflow = '';
      }
      
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      body.style.touchAction = '';
      
      html.style.overflow = '';
      html.style.touchAction = '';
      
      // Восстанавливаем позицию скролла
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = containerScrollTop;
        }
        window.scrollTo(0, scrollY);
      });
    };
  }, [open]);

  const handleClose = () => {
    // Не закрываем модалку если disableClose = true
    if (disableClose) {
      return;
    }
    onClose();
  };

  if (!open) {
    return null;
  }

  const modalContent = (
    <div
      className={styles.modalWrapper}
      onClick={disableClose ? undefined : handleClose}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalContent}>
          {!noCross && !disableClose && (
            <button
              className={styles.closeButton}
              onClick={handleClose}
            ></button>
          )}
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
